import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from 'react';
import { Button, Chip, DataGrid, Icon, MenuItem, Stack, Typography, useSnackbar } from '@emburse/embark-core';
import dayjs from 'dayjs';
import { AddIcon, FileDownloadIcon, MailIcon } from '@emburse/embark-icons';
import { IconMenu } from '../../../components';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import { TableAction } from '../../../interfaces/ManageUsers';
import { getUserRecordsXLSXBytes } from '../../../api/manageusers';
import { dateAdapterInstance } from '../../../util/dateAdapter';
import { logger } from '../../../util/logger';
const { formats } = dateAdapterInstance;
function ManageUsersTable({ data, loading, hasChanges, accountConfig, filterParameters, onNewUser, onTakeAction, onSendWelcomeEmails }) {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    const catchError = useCallback((err) => {
        logger.log(err);
        snackbarHandler(err?.Message || err, 'error');
    }, [snackbarHandler]);
    const iniitialState = () => {
        const tablePagination = localStorage.getItem('manageuserstablepagination');
        let pagination;
        if (tablePagination) {
            pagination = JSON.parse(tablePagination);
        }
        return {
            pagination: {
                paginationModel: { pageSize: pagination?.pageSize || 10 }
            }
        };
    };
    const toArrayBuffer = (base64) => {
        const binaryString = window.atob(base64);
        const binaryLen = binaryString.length;
        const bytes = new Uint8Array(binaryLen);
        for (let i = 0; i < binaryLen; i += 1) {
            const ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    };
    const downloadDataHandler = () => {
        getUserRecordsXLSXBytes(filterParameters)
            .then((recordData) => {
            const parsedData = JSON.parse(JSON.stringify(recordData));
            const fileData = new Blob([toArrayBuffer(parsedData)], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(fileData);
            link.setAttribute('download', 'UserFilterResults.xlsx');
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        })
            .catch(catchError);
    };
    const onPaginationChange = (model) => {
        const pagination = JSON.stringify(model);
        localStorage.setItem('manageuserstablepagination', pagination);
    };
    const columns = [
        {
            field: 'AccountStatus',
            headerName: $t('Status'),
            width: 90,
            renderCell: (cell) => (_jsx(Chip, { size: "mini", variant: "outlined", label: cell.row.AccountStatus ? $t('Active') : $t('Inactive'), color: cell.row.AccountStatus ? 'success' : 'monochrome' }))
        },
        {
            field: 'UserEmail',
            headerName: $t('Email'),
            flex: 3
        },
        {
            field: 'FirstName',
            headerName: $t('First Name'),
            flex: 2
        },
        {
            field: 'LastName',
            headerName: $t('Last Name'),
            flex: 2
        },
        {
            field: 'DateCreated',
            headerName: $t('Date Created'),
            flex: 2,
            renderCell: (cell) => (_jsx(Typography, { variant: "body2", children: dayjs(cell.row.DateCreated).format(formats.keyboardDate) }))
        },
        {
            field: 'EmpID',
            headerName: $t('ID #'),
            flex: 1
        },
        {
            field: 'Role',
            headerName: $t('Role'),
            flex: 2
        },
        {
            field: 'Department',
            headerName: $t('Department'),
            flex: 2
        },
        {
            field: 'ActionPlaceholder',
            renderHeader: () => (_jsx("div", { style: {
                    width: '100%',
                    textAlign: 'center'
                }, children: _jsxs(IconMenu, { anchorHorizontalPosition: "left", children: [_jsx(MenuItem, { onClick: downloadDataHandler, icon: _jsx(Icon, { iconUrl: FileDownloadIcon }), iconText: $t('Download Data'), disabled: !data.length || loading }), _jsx(MenuItem, { onClick: onSendWelcomeEmails, icon: _jsx(Icon, { iconUrl: MailIcon }), iconText: $t('Send Welcome Emails'), disabled: !data.length || loading })] }) })),
            width: 60,
            sortable: false,
            renderCell: (cell) => (_jsx("div", { style: {
                    width: '100%',
                    textAlign: 'center'
                }, children: _jsxs(IconMenu, { anchorHorizontalPosition: "left", children: [_jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.EditUser), style: { padding: '10px 16px' }, children: $t('Edit User') }), _jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.ResendWelcomeEmail), style: { padding: '10px 16px' }, children: $t('Resend Welcome Email') }), _jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.TemporaryPassword), style: { padding: '10px 16px' }, children: $t('Temporary Password') }), _jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.CreateTemporaryLoginURL), style: { padding: '10px 16px' }, children: $t('Create Temporary Login URL') }), accountConfig?.UIFlags.EmpAdminLevel === 'Full' && (_jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.MasqueradeAsUser), style: { padding: '10px 16px' }, children: $t('Switch To User') })), accountConfig.UIFlags.UseLockedWorkflow && (_jsxs(_Fragment, { children: [_jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.ApproverFor), style: { padding: '10px 16px' }, children: $t('Approver For') }), _jsx(MenuItem, { onClick: () => onTakeAction(cell.row.UserPK, TableAction.AccountantFor), style: { padding: '10px 16px' }, children: $t('Accountant For') })] }))] }) }))
        }
    ];
    return (_jsxs(_Fragment, { children: [_jsx(DataGrid, { autoHeight: true, localeText: { noRowsLabel: '' }, loading: loading, rows: data, columns: columns, pageSizeOptions: [10, 20, 30], initialState: iniitialState(), onPaginationModelChange: onPaginationChange }), !data.length && !loading && !hasChanges && (_jsxs(Stack, { spacing: 2, direction: "column", justifyContent: "space-between", sx: {
                    zIndex: 99,
                    position: 'relative'
                }, children: [_jsx(Typography, { variant: "body2", children: $t('Add a new user to get started.') }), _jsx(Button, { size: "medium", variant: "text", startIcon: _jsx(Icon, { iconUrl: AddIcon }), onClick: onNewUser, children: $t('New User') })] })), !data.length && !loading && hasChanges && (_jsx(Stack, { spacing: 2, direction: "column", justifyContent: "space-between", sx: {
                    zIndex: 99,
                    position: 'relative'
                }, children: _jsx(Typography, { variant: "body2", children: $t('No results found. Modify your filters and try again.') }) }))] }));
}
export default ManageUsersTable;
